import {
    Component,
    EventEmitter,
    forwardRef,
    inject,
    Input,
    OnChanges,
    Output,
    signal,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import { provideTranslocoScope, TranslocoPipe } from "@jsverse/transloco";
import { CerrixLabelComponent } from "@app/shared/atoms/cerrix-label/cerrix-label.component";
import { CerrixPickerFieldComponent } from "@app/shared/atoms/cerrix-picker-field/cerrix-picker-field.component";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { CerrixCheckboxComponent } from "@app/shared/atoms/cerrix-checkbox/cerrix-checkbox.component";
import { CerrixSearchInputComponent } from "@app/shared/atoms/cerrix-search-input/cerrix-search-input.component";
import { CerrixSelectTreeComponent } from "@app/shared/molecules/cerrix-select-tree/cerrix-select-tree.component";
import { CerrixDialogComponent } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog.component";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { CerrixTreeItem } from "@models/tree/CerrixTreeItem";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { CerrixInstructionsIconComponent } from "@app/shared/cerrix-instructions-icon/cerrix-instructions-icon.component";
import { CerrixInputValidationErrorsDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-errors.directive";
import {
    CERRIX_INPUT_VALUE_SOURCE,
    CerrixInputValueSource,
} from "@app/shared/directives/cerrix-input-fields/models/cerrix-input-value-source.interface";
import { Subject } from "rxjs";

/**
 Note: this component is not intended to use directly on a page. 
 Use the Single or Multi CerrixTreeItemPicker components instead.
 */

@Component({
    selector: "app-cerrix-tree-item-picker",
    templateUrl: "./cerrix-tree-item-picker.component.html",
    styleUrls: ["./cerrix-tree-item-picker.component.scss"],
    imports: [
        TranslocoPipe,
        CerrixLabelComponent,
        CerrixPickerFieldComponent,
        CerrixButtonComponent,
        CerrixCheckboxComponent,
        CerrixSearchInputComponent,
        CerrixSelectTreeComponent,
        CerrixDialogComponent,
        MatIconModule,
        MatTooltipModule,
        CerrixInstructionsIconComponent,
        CerrixInputValidationErrorsDirective,
    ],
    providers: [
        provideTranslocoScope({
            scope: "shared/organisms/cerrix-tree-item-picker",
            alias: "cerrix-tree-item-picker",
        }),
        {
            provide: CERRIX_INPUT_VALUE_SOURCE,
            useExisting: forwardRef(() => CerrixTreeItemPickerComponent),
        },
    ],
    encapsulation: ViewEncapsulation.None,
})
export class CerrixTreeItemPickerComponent implements OnChanges, CerrixInputValueSource<string[]> {
    @Input() name = "tree-item-picker";
    @Input() label: string;
    @Input() infoText: string = "";
    @Input() placeholder: string = "";
    @Input() dialogTitle: string;
    @Input() iconPrefix = "";
    @Input() defaultIcon = "";
    @Input() items: CerrixTreeItem[] = [];
    @Input() multiselect: boolean = false;
    @Input() selectedValue: string[];
    @Output() valueChanged: EventEmitter<string[]> = new EventEmitter<string[]>();

    @ViewChild("selectTree") private selectTree: CerrixSelectTreeComponent;
    private _dialogService = inject(CerrixDialogService);

    protected draftSelection: string[] = [];
    protected filter: string = "";
    protected includeChildren = false;
    protected showSelectedOnly = false;
    protected hadSelectionOnOpen = false;

    // The picked items live in this component, so the required validator watches them here
    // rather than the names the picker field happens to show.
    private pickedValue = signal<string[]>([]);
    readonly validationValue = this.pickedValue.asReadonly();

    // Pressing the field opens the dialog that fills it, so the blur that follows means
    // nothing; the dialog closing, however it closes, is the end of the interaction.
    readonly validationTouched = new Subject<void>();

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["selectedValue"]) {
            this.pickedValue.set(this.selectedValue ?? []);
        }
    }

    protected get dialogName(): string {
        return `${this.name}-dialog`;
    }

    protected get confirmButtonLabelKey(): string {
        return this.hadSelectionOnOpen
            ? "cerrix-tree-item-picker.updateButton"
            : "cerrix-tree-item-picker.addButton";
    }

    public openDialog(): void {
        const selectedValue = this.selectedValue;
        this.draftSelection = selectedValue ? [...selectedValue] : [];
        this.hadSelectionOnOpen = this.draftSelection.length > 0;
        this.showSelectedOnly = false;
        this._dialogService.openDialog(this.dialogName);
    }

    public cancelSelection(): void {
        this._dialogService.closeDialog(this.dialogName);
    }

    public confirmSelection(): void {
        const selection = [...(this.draftSelection ?? [])];
        // Closing the dialog below validates straight away, before the new selectedValue
        // can flow back in, so the validator is handed the pick here.
        this.pickedValue.set(selection);
        this.valueChanged.emit(selection);
        this._dialogService.closeDialog(this.dialogName);
    }

    protected updateDraftSelection(selection: string[]): void {
        this.draftSelection = selection;
    }

    protected expandAll(): void {
        this.showSelectedOnly = false;
        this.selectTree.expandAll();
    }

    protected selectAll(): void {
        this.selectTree.selectAll();
    }

    protected toggleShowSelectedOnly(): void {
        this.showSelectedOnly = !this.showSelectedOnly;
    }

    protected toggleIncludeChildren(): void {
        this.includeChildren = !this.includeChildren;
    }

    protected onFilterChange(value: string): void {
        this.filter = value;
    }

    private findItem(
        guid: string,
        items: CerrixTreeItem[] = this.items,
    ): CerrixTreeItem | undefined {
        for (const item of items) {
            if (item.Guid === guid) {
                return item;
            }

            const childItem = item.Children?.length
                ? this.findItem(guid, item.Children)
                : undefined;
            if (childItem) {
                return childItem;
            }
        }

        return undefined;
    }

    protected selectedItemsDescription(): string {
        const toDisplayValue = (item: CerrixTreeItem | undefined): string => {
            return item?.Name ?? "";
        };

        return (this.selectedValue ?? [])
            .map((guid) => toDisplayValue(this.findItem(guid)))
            .join(", ");
    }
}
